'use client';

import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

type BillingCycle = 'HALF_YEARLY' | 'YEARLY';

interface PlanPrice {
  mrp: number;
  selling: number;
}

interface PricingPlan {
  id: 'plus' | 'prime' | 'pro';
  name: string;
  description: string;
  popular?: boolean;
  prices?: Record<BillingCycle, PlanPrice>;
  features: string[];
}

interface PricingCardsProps {
  plans: PricingPlan[];
}

export default function PricingCards({ plans }: PricingCardsProps) {
  const formatINR = (amount: number) => amount.toLocaleString('en-IN');

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-5xl font-heading font-extrabold text-primary mb-4">
            Pricing for every type of driver
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Plans cover service activation. Physical card cost is a one-time add-on on your first purchase.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={[
                'relative rounded-2xl p-8 border transition-all duration-300',
                'bg-card shadow-subtle hover:shadow-card hover:-translate-y-1',
                plan.popular
                  ? 'border-accent/40 ring-2 ring-accent/20'
                  : 'border-border hover:border-accent/25',
              ].join(' ')}
            >
              {plan.popular ? (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-accent text-accent-foreground px-6 py-2 rounded-full text-sm font-semibold shadow-button">
                    Most Popular
                  </span>
                </div>
              ) : null}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-heading font-extrabold text-foreground mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-muted-foreground">{plan.description}</p>
              </div>

              {plan.prices ? (
                <div className="rounded-2xl border border-border bg-background p-4 mb-8">
                  <div className="space-y-3">
                    {(['HALF_YEARLY', 'YEARLY'] as const).map((cycle) => {
                      const row = plan.prices?.[cycle];
                      const label = cycle === 'YEARLY' ? '12 months' : '6 months';
                      if (!row) return null;
                      const pct = Math.round(((row.mrp - row.selling) / row.mrp) * 100);
                      return (
                        <div key={cycle} className="flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-foreground">{label}</div>
                            <div className="text-[11px] text-muted-foreground">
                              MRP <span className="line-through tabular-nums">INR {formatINR(row.mrp)}</span>
                              <span className="ml-2 inline-flex items-center rounded-full bg-accent/10 border border-accent/20 px-2 py-0.5 font-semibold text-foreground tabular-nums">
                                Save {pct}%
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-[11px] text-muted-foreground">Plan fee</div>
                            <div className="text-lg font-heading font-extrabold text-foreground tabular-nums">
                              INR {formatINR(row.selling)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-3 border-t border-border/70 text-[11px] text-muted-foreground">
                    One-time card cost (first purchase): INR 99
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-border bg-background p-4 mb-8">
                  <div className="text-sm font-semibold text-foreground">Custom pricing</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    For societies and enterprise deployments.
                  </div>
                </div>
              )}

              <ul className="space-y-3 mb-10">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-accent/10 rounded-full border border-accent/20 flex items-center justify-center mt-0.5">
                      <Icon name="CheckIcon" size={14} className="text-accent" />
                    </div>
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              {plan.id === 'pro' ? (
                <a
                  href="mailto:support@parkping.co.in"
                  className="block w-full text-center py-4 rounded-xl font-heading font-semibold text-base transition-all duration-300 bg-primary text-primary-foreground hover:brightness-105 shadow-subtle"
                >
                  Contact sales
                </a>
              ) : (
                <Link
                  href="/get-started"
                  className={[
                    'block w-full text-center py-4 rounded-xl font-heading font-semibold text-base transition-all duration-300',
                    plan.popular
                      ? 'bg-accent text-accent-foreground shadow-cta hover:shadow-cta-hover hover:-translate-y-0.5'
                      : 'bg-primary text-primary-foreground shadow-subtle hover:-translate-y-0.5',
                  ].join(' ')}
                >
                  Get ParkPing
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
